import { useEffect, useState, useMemo } from "react";
import { IconButton } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useOrders, useUpdateOrder } from "api/useOrders.ts";
import { useProducts } from "api/useProducts.ts";
import MDBox from "components/MDBox";
import MDBadge from "components/MDBadge";
import EditIcon from "@mui/icons-material/Edit";

export default function appData() {
  const { data: orders, isLoading: loadingOrders } = useOrders();
  const { data: products } = useProducts();
  const updateOrder = useUpdateOrder();
  const [selectedProducts, setSelectedProducts] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editedOrder, setEditedOrder] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success", // "success" | "error"
  });

  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleViewProducts = (products) => {
    setSelectedProducts(products);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedProducts(null);
    setIsModalOpen(false);
  };

  const getSelectedProducts = () => {
    return new Promise((resolve) => {
      const filteredProducts = products
        .filter((product) => selectedProducts?.some((p) => p.product._id === product._id))
        .map((product) => {
          const selectedProduct = selectedProducts.find((p) => p.product._id === product._id);
          return {
            ...product,
            quantity: selectedProduct?.quantity ?? product.quantity,
          };
        });

      resolve(filteredProducts);
    });
  };

  const handleEditClick = (order) => {
    setSelectedOrder(order);
    setEditedOrder({
      orderStatus: order.orderStatus,
    });
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedOrder(null);
    setEditedOrder(null);
  };

  const handleSaveChanges = async () => {
    try {
      // Ensure the status is one of the allowed values
      const validStatuses = ["Pending", "Paid", "Cancelled", "Completed"];
      if (!validStatuses.includes(editedOrder.orderStatus)) {
        throw new Error(`Invalid order status: ${editedOrder.orderStatus}`);
      }

      const updatedOrder = await updateOrder.mutateAsync({
        id: selectedOrder._id,
        orderStatus: editedOrder.orderStatus,
      });

      setEditedOrder(updatedOrder);
      setNotification({
        open: true,
        message: "Order updated successfully",
        severity: "success",
      });

      handleCloseEditModal();
    } catch (error) {
      console.error("Error updating order:", error);
      setNotification({
        open: true,
        message: error.message || "Error updating order",
        severity: "error",
      });
    }
  };

  const rows = useMemo(() => {
    if (loadingOrders) {
      return [
        {
          orderId: "Loading...",
          userId: "Loading...",
          customer: "Loading...",
          totalPrice: "Loading...",
          orderDate: "Loading...",
          paymentMethod: "Loading...",
          orderStatus: (
            <MDBox ml={-1}>
              <MDBadge badgeContent="loading" color="light" variant="gradient" size="sm" />
            </MDBox>
          ),
          mosque: "Loading...",
          products: "Loading...",
          actions: "Loading...",
        },
      ];
    }

    return orders?.map((order) => ({
      orderId: order._id,
      userId: order.customer._id,
      customer: order.customer?.name || "Unknown Customer",
      totalPrice: `$${order.totalAmount.toFixed(2)}`,
      orderDate: new Date(order.orderDate).toLocaleDateString(),
      paymentMethod: order.paymentMethod,
      orderStatus: (
        <MDBadge
          badgeContent={order.orderStatus}
          color={
            order.orderStatus === "Pending"
              ? "warning"
              : order.orderStatus === "Paid"
              ? "success"
              : order.orderStatus === "Cancelled"
              ? "error"
              : order.orderStatus === "Completed"
              ? "info"
              : "light"
          }
          variant="gradient"
          size="sm"
        />
      ),
      mosque: order.mosque.name,
      products: (
        <ul>
          {order.products.map((product) => (
            <li key={product.product._id}>
              {product.product?.name?.en || "Unknown Product"} Qty: {product.quantity}
            </li>
          ))}
        </ul>
      ),
      actions: (
        <div className="flex justify-center space-x-2">
          <IconButton color="secondary" onClick={() => handleViewProducts(order.products)}>
            <VisibilityIcon />
          </IconButton>
          <IconButton color="primary" size="medium" onClick={() => handleEditClick(order)}>
            <EditIcon fontSize="medium" />
          </IconButton>
        </div>
      ),
    }));
  }, [loadingOrders, orders]);

  return {
    columns: [
      { Header: "Order ID", accessor: "orderId", align: "center" },
      { Header: "User ID", accessor: "userId", align: "center" },
      { Header: "Customer", accessor: "customer", align: "center" },
      { Header: "Total Price", accessor: "totalPrice", align: "center" },
      { Header: "Order Date", accessor: "orderDate", align: "center" },
      { Header: "Payment Method", accessor: "paymentMethod", align: "center" },
      { Header: "Order Status", accessor: "orderStatus", align: "center" },
      { Header: "Mosque", accessor: "mosque", align: "center" },
      { Header: "Products", accessor: "products", align: "center" },
      { Header: "Actions", accessor: "actions", align: "center" },
    ],
    rows,
    isModalOpen,
    handleCloseModal,
    fetchProducts: getSelectedProducts,
    handleCloseEditModal,
    isEditModalOpen,
    editedOrder,
    notification,
    setNotification,
    handleSaveChanges,
    setEditedOrder,
  };
}
